import React, { useContext, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import HeartIcon from 'mdi-react/HeartIcon';
import HeartOutlineIcon from 'mdi-react/HeartOutlineIcon';
import BaseButton from '../base-button';
import MyProductsIcon from './my-products-icon';
import { ViewportContext } from '../../viewport';
import LoginPromoModal from '../../modal/login-promo';
import {
  selectList,
  addListItemRequest,
  addItemToMyProductsRequest,
} from '../../../areas/sidebar/actions/lists';
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated';
import { AppState } from '../../../reducers';

import * as S from '../styles';
import { TooltipAlign, TooltipPlacement } from 'src/components/tooltip-new';

type List = { id: number; name: string; inventory: boolean };

type Me = {
  lists: {
    nodes: List[];
  };
};

type Props = {
  dropdownGrow?: 'left' | 'right';
  productId: number;
  productName: string;
  tooltipAlign?: TooltipAlign;
  tooltipPlacement?: TooltipPlacement;
};

export const AddToListButton = ({
  dropdownGrow = 'left',
  productId,
  productName,
  tooltipAlign,
  tooltipPlacement,
}: Props) => {
  const { t } = useAppContext();
  const { isDesktop } = useContext(ViewportContext);
  const dispatch = useDispatch();
  const [showLoginPromo, setLoginPromoState] = useState(false);
  const toggleLoginPromo = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    setLoginPromoState(!showLoginPromo);
  };

  // Highlights the heart if the product was recently added to a list.
  // Kind of an ugly hack; should be fixed once we can get this information
  // from GraphQL.
  const [addedToList, setAddedToListState] = useState<boolean>(false);

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const isAuthenticated = useIsAuthenticated();
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );
  const latestLists: List[] = me?.lists?.nodes
    .filter(list => deletedLists[list.id] !== true)
    .filter(list => !list.inventory)
    .sort(a => {
      return a.id === selectedList ? 1 : 0;
    });

  const onButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    addToList(productId, productName);
  };

  const addToMyProducts = useCallback(
    (id: number, name: string) => {
      dispatch(addItemToMyProductsRequest(id, name, isDesktop));
      setAddedToListState(true);
    },
    [dispatch, isDesktop]
  );

  const addToList = useCallback(
    (id: number, name: string) => {
      dispatch(addListItemRequest(id, name, isDesktop));
      setAddedToListState(true);
    },
    [dispatch, isDesktop]
  );

  if (!productId) return null;

  if (isAuthenticated) {
    const myProductsDropDownItem = (
      <S.DropdownItem
        onClick={() => {
          addToMyProducts(productId, productName);
        }}
        key="my-products"
      >
        <S.IconWrapper>
          <MyProductsIcon size={24} />
        </S.IconWrapper>
        {t('lists', 'my-products.title')}
      </S.DropdownItem>
    );

    const listDropDownItems = latestLists
      ? latestLists.map(list => (
          <S.DropdownItem
            onClick={() => {
              dispatch(selectList(list.id));
              addToList(productId, productName);
            }}
            key={list.id}
          >
            {list.name}
          </S.DropdownItem>
        ))
      : [];

    return (
      <S.Dropdown
        variant="secondary"
        grow={dropdownGrow}
        showArrow={false}
        toggleElement={
          <BaseButton
            isActive={addedToList}
            icon={
              addedToList ? (
                <HeartIcon color="secondary" />
              ) : (
                <HeartOutlineIcon color="secondary" />
              )
            }
            ariaLabel={t('common', 'button.save')}
            onClick={listDropDownItems.length > 0 ? undefined : onButtonClick}
            tooltip={t('product', 'action-button.list-tooltip')}
            tooltipAlign={tooltipAlign}
            tooltipPlacement={tooltipPlacement}
          />
        }
      >
        {[myProductsDropDownItem, ...listDropDownItems]}
      </S.Dropdown>
    );
  }
  return (
    <>
      {showLoginPromo && (
        <LoginPromoModal
          isOpen={showLoginPromo}
          onRequestClose={toggleLoginPromo}
        />
      )}
      <BaseButton
        isActive={addedToList}
        icon={
          addedToList ? (
            <HeartIcon color="secondary" />
          ) : (
            <HeartOutlineIcon color="secondary" />
          )
        }
        ariaLabel={t('common', 'button.save')}
        onClick={toggleLoginPromo}
        tooltip={t('product', 'action-button.list-tooltip')}
        tooltipAlign={tooltipAlign}
        tooltipPlacement={tooltipPlacement}
      />
    </>
  );
};
